
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";
import { Smartphone } from "lucide-react";
import { validateEmail, validateUsername, sanitizeInput } from "@/utils/inputValidation";

export const AuthForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const validateSignUpForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!validateEmail(email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters long";
    }
    
    const usernameValidation = validateUsername(username);
    if (!usernameValidation.isValid) {
      newErrors.username = usernameValidation.error || "Invalid username";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateSignInForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!validateEmail(email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    if (password.length === 0) {
      newErrors.password = "Password is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateSignUpForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const sanitizedUsername = sanitizeInput(username);
      const { error } = await supabase.auth.signUp({
        email: email.toLowerCase().trim(),
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/discovery`,
          data: {
            username: sanitizedUsername,
          },
        },
      });
      
      if (error) throw error;
      
      toast({
        title: "Success!",
        description: "Check your email for the confirmation link.",
      });
    } catch (error: any) {
      console.error('Sign up error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create account",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateSignInForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password,
      });
      
      if (error) throw error;
      
      toast({
        title: "Welcome back!",
        description: "You have successfully signed in.",
      });
    } catch (error: any) {
      console.error('Sign in error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to sign in",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthSignIn = async (provider: 'apple' | 'google') => {
    setLoading(true);
    
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/discovery`,
        },
      });
      
      if (error) throw error;
    } catch (error: any) {
      console.error(`${provider} sign in error:`, error);
      toast({
        title: "Error",
        description: error.message || `Failed to sign in with ${provider}`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto relative z-10">
      {/* BDSM-themed metallic card */}
      <Card className="relative overflow-hidden border-0 shadow-2xl bg-carbon">
        {/* Neon red metallic border gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-neon-red/40 via-electric-violet/30 to-neon-red/40 p-[2px] rounded-lg">
          <div className="h-full w-full rounded-lg bg-carbon"></div>
        </div>
        
        {/* Inner content with BDSM dark theme */}
        <div className="relative bg-carbon rounded-lg">
          <CardContent className="p-8 bg-carbon text-ice-white">
            <Tabs defaultValue="signin" className="w-full" data-testid="auth-form">
              {/* BDSM-themed metallic tabs */}
              <TabsList className="grid w-full grid-cols-2 bg-carbon/80 p-1 h-auto mb-8 rounded-lg border border-neon-red/30 backdrop-blur-sm" data-testid="auth-tabs">
                <TabsTrigger 
                  value="signin" 
                  className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-neon-red/80 data-[state=active]:to-neon-red/60 data-[state=active]:text-ice-white data-[state=active]:shadow-lg data-[state=active]:shadow-neon-red/40 data-[state=active]:border data-[state=active]:border-neon-red/50 bg-carbon/60 text-ice-white/70 border border-ice-white/20 rounded-md py-3 px-4 font-semibold text-sm transition-all duration-300 hover:bg-carbon/80 hover:text-ice-white hover:border-neon-red/30 hover:shadow-neon-red/20"
                >
                  Sign In
                </TabsTrigger>
                <TabsTrigger 
                  value="signup"
                  className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-electric-violet/80 data-[state=active]:to-toxic-green/60 data-[state=active]:text-ice-white data-[state=active]:shadow-lg data-[state=active]:shadow-electric-violet/40 data-[state=active]:border data-[state=active]:border-electric-violet/50 bg-carbon/60 text-ice-white/70 border border-ice-white/20 rounded-md py-3 px-4 font-semibold text-sm transition-all duration-300 hover:bg-carbon/80 hover:text-ice-white hover:border-electric-violet/30 hover:shadow-electric-violet/20"
                >
                  Sign Up
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="signin" className="mt-6 space-y-6">
                {/* BDSM-themed OAuth Buttons */}
                <div className="space-y-4">
                  <Button 
                    variant="outline" 
                    className="w-full flex items-center justify-center space-x-3 bg-carbon/80 text-ice-white border-2 border-ice-white/30 hover:bg-carbon/60 hover:border-neon-red/50 hover:shadow-lg hover:shadow-neon-red/30 backdrop-blur-sm transition-all duration-300 py-6 font-medium text-sm"
                    onClick={() => handleOAuthSignIn('apple')}
                    disabled={loading}
                  >
                    <Smartphone className="w-5 h-5 text-ice-white" />
                    <span className="text-ice-white">Continue with Apple</span>
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="w-full flex items-center justify-center space-x-3 bg-carbon/80 text-ice-white border-2 border-ice-white/30 hover:bg-carbon/60 hover:border-toxic-green/50 hover:shadow-lg hover:shadow-toxic-green/30 backdrop-blur-sm transition-all duration-300 py-6 font-medium text-sm"
                    onClick={() => handleOAuthSignIn('google')}
                    disabled={loading}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span className="text-ice-white">Continue with Google</span>
                  </Button>
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <Separator className="w-full bg-ice-white/30" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-carbon px-4 text-ice-white/60 font-medium">
                      Or continue with email
                    </span>
                  </div>
                </div>

                <form onSubmit={handleSignIn} className="space-y-5" data-testid="signin-form">
                  <div>
                    <Input
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (errors.email) setErrors(prev => ({...prev, email: ''}));
                      }}
                      className={`bg-carbon/60 border-2 border-ice-white/30 text-ice-white placeholder:text-ice-white/50 focus:border-neon-red/70 focus:ring-neon-red/30 focus:ring-2 backdrop-blur-sm ${errors.email ? "border-neon-red/80" : ""} py-3 h-12 rounded-lg`}
                      data-testid="signin-email"
                      required
                    />
                    {errors.email && <p className="text-neon-red/80 text-sm mt-2 font-medium">{errors.email}</p>}
                  </div>
                  <div>
                    <Input
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (errors.password) setErrors(prev => ({...prev, password: ''}));
                      }}
                      className={`bg-carbon/60 border-2 border-ice-white/30 text-ice-white placeholder:text-ice-white/50 focus:border-neon-red/70 focus:ring-neon-red/30 focus:ring-2 backdrop-blur-sm ${errors.password ? "border-neon-red/80" : ""} py-3 h-12 rounded-lg`}
                      data-testid="signin-password"
                      required
                    />
                    {errors.password && <p className="text-neon-red/80 text-sm mt-2 font-medium">{errors.password}</p>}
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-neon-red/90 to-neon-red/70 hover:from-neon-red hover:to-neon-red/80 text-ice-white font-semibold py-3 h-12 shadow-lg hover:shadow-xl hover:shadow-neon-red/40 transition-all duration-300 border border-neon-red/50" 
                    disabled={loading}
                    data-testid="signin-button"
                  >
                    {loading ? "Signing in..." : "Sign In"}
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="signup" className="mt-6 space-y-6">
                {/* BDSM-themed OAuth Buttons */}
                <div className="space-y-4">
                  <Button 
                    variant="outline" 
                    className="w-full flex items-center justify-center space-x-3 bg-carbon/80 text-ice-white border-2 border-ice-white/30 hover:bg-carbon/60 hover:border-electric-violet/50 hover:shadow-lg hover:shadow-electric-violet/30 backdrop-blur-sm transition-all duration-300 py-6 font-medium text-sm"
                    onClick={() => handleOAuthSignIn('apple')}
                    disabled={loading}
                  >
                    <Smartphone className="w-5 h-5 text-ice-white" />
                    <span className="text-ice-white">Sign up with Apple</span>
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="w-full flex items-center justify-center space-x-3 bg-carbon/80 text-ice-white border-2 border-ice-white/30 hover:bg-carbon/60 hover:border-toxic-green/50 hover:shadow-lg hover:shadow-toxic-green/30 backdrop-blur-sm transition-all duration-300 py-6 font-medium text-sm"
                    onClick={() => handleOAuthSignIn('google')}
                    disabled={loading}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span className="text-ice-white">Sign up with Google</span>
                  </Button>
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <Separator className="w-full bg-ice-white/30" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-carbon px-4 text-ice-white/60 font-medium">
                      Or sign up with email
                    </span>
                  </div>
                </div>

                <form onSubmit={handleSignUp} className="space-y-5">
                  <div>
                    <Input
                      type="text"
                      placeholder="Username"
                      value={username}
                      onChange={(e) => {
                        setUsername(e.target.value);
                        if (errors.username) setErrors(prev => ({...prev, username: ''}));
                      }}
                      className={`bg-carbon/60 border-2 border-ice-white/30 text-ice-white placeholder:text-ice-white/50 focus:border-electric-violet/70 focus:ring-electric-violet/30 focus:ring-2 backdrop-blur-sm ${errors.username ? "border-neon-red/80" : ""} py-3 h-12 rounded-lg`}
                      required
                    />
                    {errors.username && <p className="text-neon-red/80 text-sm mt-2 font-medium">{errors.username}</p>}
                  </div>
                  <div>
                    <Input
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (errors.email) setErrors(prev => ({...prev, email: ''}));
                      }}
                      className={`bg-carbon/60 border-2 border-ice-white/30 text-ice-white placeholder:text-ice-white/50 focus:border-electric-violet/70 focus:ring-electric-violet/30 focus:ring-2 backdrop-blur-sm ${errors.email ? "border-neon-red/80" : ""} py-3 h-12 rounded-lg`}
                      required
                    />
                    {errors.email && <p className="text-neon-red/80 text-sm mt-2 font-medium">{errors.email}</p>}
                  </div>
                  <div>
                    <Input
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (errors.password) setErrors(prev => ({...prev, password: ''}));
                      }}
                      className={`bg-carbon/60 border-2 border-ice-white/30 text-ice-white placeholder:text-ice-white/50 focus:border-electric-violet/70 focus:ring-electric-violet/30 focus:ring-2 backdrop-blur-sm ${errors.password ? "border-neon-red/80" : ""} py-3 h-12 rounded-lg`}
                      required
                    />
                    {errors.password && <p className="text-neon-red/80 text-sm mt-2 font-medium">{errors.password}</p>}
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-electric-violet/90 to-toxic-green/70 hover:from-electric-violet hover:to-toxic-green/80 text-ice-white font-semibold py-3 h-12 shadow-lg hover:shadow-xl hover:shadow-electric-violet/40 transition-all duration-300 border border-electric-violet/50" 
                    disabled={loading}
                  >
                    {loading ? "Creating account..." : "Sign Up"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </div>
      </Card>
    </div>
  );
};
